import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { REAL_BASE_URL } from '../services/api';
import { getVisibilidadPantallas } from '../services/pantallasService';
import { useAuth } from './AuthContext';
import { esPantallaVisible, estadoPantalla } from '../utils/pantallas';

const REINTENTO_WS_MS = 5000;

const PantallasContext = createContext({
  pantallas: null, cargado: false, recargar: () => {}, esVisible: () => true, estado: () => 'VISIBLE',
});

// Visibilidad de pantallas según el rol (sin login = público). Se vuelve a
// pedir al backend al cambiar de rol, en cada cambio de pantalla (ver
// GuardaPantalla) y cuando el WebSocket avisa de que la tabla `pantallas`
// ha cambiado: el aviso no lleva datos, cada cliente pregunta con su sesión.
export function PantallasProvider({ children }) {
  const { usuario, cargando } = useAuth();
  const rol = usuario?.rol ?? null;
  // null = aún sin datos o no se pudo cargar (todo visible).
  const [pantallas, setPantallas] = useState(null);
  const [cargado, setCargado] = useState(false);
  const peticion = useRef(0);

  const recargar = useCallback(() => {
    const esta = ++peticion.current;
    return getVisibilidadPantallas()
      .then((r) => { if (esta === peticion.current) setPantallas(r.datos); })
      // Sin respuesta se deja pasar.
      .catch(() => { if (esta === peticion.current) setPantallas(null); })
      .finally(() => { if (esta === peticion.current) setCargado(true); });
  }, []);

  // Hasta saber quién es el usuario, pedirla como público mostraría de más.
  useEffect(() => { if (!cargando) recargar(); }, [cargando, rol, recargar]);

  useEffect(() => {
    let ws;
    let temporizador;
    let cerrado = false;

    const conectar = () => {
      try {
        ws = new WebSocket(`${REAL_BASE_URL.replace(/^http/, 'ws')}/ws`);
      } catch {
        return;
      }
      ws.onmessage = (e) => {
        try { if (JSON.parse(e.data).tipo === 'pantallas') recargar(); } catch { /* mensaje ajeno */ }
      };
      ws.onopen = recargar; // por si hubo cambios mientras estaba caído
      ws.onclose = () => { if (!cerrado) temporizador = setTimeout(conectar, REINTENTO_WS_MS); };
    };

    conectar();
    return () => {
      cerrado = true;
      clearTimeout(temporizador);
      ws?.close();
    };
  }, [recargar]);

  const esVisible = useCallback((pathname) => esPantallaVisible(pantallas, pathname), [pantallas]);
  const estado = useCallback((pathname) => estadoPantalla(pantallas, pathname), [pantallas]);

  return (
    <PantallasContext.Provider value={{ pantallas, cargado, recargar, esVisible, estado }}>
      {children}
    </PantallasContext.Provider>
  );
}

export function usePantallas() {
  return useContext(PantallasContext);
}
